import React from "react"
import { useTranslation } from "react-i18next"
import {
  withFormik,
  FormikProps,
  FormikErrors,
  Form,
  Field,
  FieldProps,
} from "formik"
import { Grid } from "@mui/material"

import i18n from "config/i18n"
import InputSelect from "components/ui/InputSelect"
import Input from "components/ui/Input"
import { Card, Headline, Submit } from "components/Sign/elements"
import { smartIdCountryOptions } from "helpers/countries"
import { PERSONAL_CODE_LENGTH, PERSONAL_CODE_REGEX } from "helpers/client"
import { ClientSmartIdFormValues, SelectOptionType } from "types"

interface FormProps {
  initialValues: {
    country?: string
    personal_code?: string
  }
  onSubmit: (values: object) => void
}

type Props = FormikProps<ClientSmartIdFormValues> & FormProps

const SmartIdForm = ({ errors, isSubmitting }: Props) => {
  const { t } = useTranslation()
  const countryOptions = smartIdCountryOptions()

  return (
    <Form>
      <Card elevation={0}>
        <Headline variant="h2">{t("providers.smart_id.title")}</Headline>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Field name="country">
              {({ field }: FieldProps) => (
                <InputSelect light fullWidth disabled={isSubmitting} {...field}>
                  {countryOptions.map((c: SelectOptionType) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </InputSelect>
              )}
            </Field>
          </Grid>
          <Grid item xs={6}>
            <Field name="personal_code">
              {({ field }: FieldProps) => (
                <Input
                  light
                  fullWidth
                  label={t("features.client.personal_code")}
                  helperText={errors.personal_code}
                  disabled={isSubmitting}
                  {...field}
                />
              )}
            </Field>
          </Grid>
        </Grid>
      </Card>
      <Submit type="submit" disabled={isSubmitting}>
        {t("common.sign")}
      </Submit>
    </Form>
  )
}

const SmartIdFormWithFormik = withFormik<FormProps, ClientSmartIdFormValues>({
  enableReinitialize: true,
  mapPropsToValues: ({ initialValues }) => initialValues,
  handleSubmit: (values, { props: { onSubmit } }) => onSubmit(values),
  validate: (values: ClientSmartIdFormValues) => {
    const errors: FormikErrors<ClientSmartIdFormValues> = {}
    const { personal_code } = values

    if (!personal_code) {
      errors.personal_code = i18n.t("validation.field.required", {
        field: i18n.t("features.client.personal_code"),
      })
    } else if (
      !PERSONAL_CODE_REGEX.test(personal_code) ||
      personal_code.length != PERSONAL_CODE_LENGTH
    ) {
      errors.personal_code = i18n.t("validation.field.invalid", {
        field: i18n.t("features.client.personal_code"),
      })
    }

    return errors
  },
})(SmartIdForm)

export default SmartIdFormWithFormik
